import usePackageAreaConverter from '~/composeables/usePackageAreaConverter';
import useApiFetch from '~/composeables/useApiFetch';
import { useUiStore } from '~/store/ui';
import { useGlobalContentStore } from '~/store/globalContent';
import { useCartStore } from '~/store/cart';
import {
  CartApiItem,
  DiscountQuantityBreak,
  DiscountQuantityBreakApiResponse
} from '~/constants/types/norce';

export default function useQuantityBreaks(item: Readonly<Ref<CartApiItem>>) {
  const { apiGet } = useApiFetch();
  const globalContent = useGlobalContentStore();
  const uiStore = useUiStore();
  const cartStore = useCartStore();
  const { unitDisplay, convertPackagePriceToAreaPrice } = usePackageAreaConverter(toRef(()=> item.value));

  const discountCBraw = ref<DiscountQuantityBreakApiResponse[]>([]);
  const checkDiscountLevels = async() => {
    const res = await apiGet<DiscountQuantityBreakApiResponse[]>(`/products/${item.value.partNo}/quantity-breaks`);
    if (res) {
      discountCBraw.value = res;
    } else {
      console.warn(`Couldn't load quantity-breaks on ${item.value.partNo}`);
    }

    return discountCBraw;
  };

  const discountQuantityBreaks = computed<DiscountQuantityBreak[]>(()=> {
    return discountCBraw.value.map((i: DiscountQuantityBreakApiResponse) => {
      return {
        ...i,
        // if we reach the step by combining what's in the cart and what's about to be added
        reached: cartStore.getQuantityFromPartNo(item.value.partNo) >= i.quantity,
        areaPrice: convertPackagePriceToAreaPrice(globalContent.getDisplayIncVat ? i.price : i.priceBeforeVat),
        unitDisplay,
      };
    });
  });

  const reachedDiscountPercent = computed(()=> {
    const percent = discountQuantityBreaks.value.filter(f => f.reached === true).map(m => m.discountPercentage);
    percent.push(0);
    return Math.max(...percent);
  });

  const reachedDiscountPrice = computed(()=> {
    uiStore.overrideDisplayPrice = -1;
    const reached = discountQuantityBreaks.value.filter(f => f.reached === true)
      .map(m => parseFloat(globalContent.getDisplayIncVat ? m.price : m.priceBeforeVat));
    if (!reached.length) {
      return null;
    }

    const newPrice = Math.min(...reached);
    uiStore.overrideDisplayPrice = newPrice;
    return newPrice;
  });

  const firstUnreachedQuantityBreak = computed(()=> {
    return discountQuantityBreaks.value.filter(f => f.reached !== true)[0] ?? null;
  });

  return {
    checkDiscountLevels,
    reachedDiscountPercent,
    reachedDiscountPrice,
    discountQuantityBreaks,
    firstUnreachedQuantityBreak,
  };
}
